import React from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { FaLinkedin, FaGithub } from 'react-icons/fa6';

function Hero() {
  const controls = useAnimation();
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  React.useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section
      id="home"
      className="min-h-screen relative overflow-hidden bg-arcade-bg crt-scanlines"
    >
      {/* faint grid */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.06] pointer-events-none"></div>

      {/* decorative pixels */}
      <div className="absolute top-[16%] left-[8%] w-3 h-3 bg-arcade-pink"></div>
      <div className="absolute top-[11%] right-[42%] w-2.5 h-2.5 bg-arcade-green"></div>
      <div className="absolute bottom-[18%] left-[46%] w-2 h-2 bg-arcade-yellow"></div>

      <motion.div
        ref={ref}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_420px] items-center gap-12 lg:gap-20 py-16"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        {/* Left: title block */}
        <div>
          <motion.p
            variants={itemVariants}
            className="font-pixel text-arcade-yellow text-sm md:text-base mb-6 flex items-center gap-3"
          >
            <span className="inline-block w-2.5 h-2.5 bg-arcade-yellow"></span>
            INSERT COIN
          </motion.p>

          <motion.h1
            variants={itemVariants}
            className="font-pixel text-arcade-cyan text-4xl md:text-6xl leading-tight mb-6 drop-shadow-[0_0_12px_rgba(77,232,244,0.45)]"
          >
            NURI ONACAK
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="font-pixel text-arcade-yellow text-sm md:text-lg tracking-wider mb-8"
          >
            {t('hero.classLabel')} · {t('hero.profession').toUpperCase()}
          </motion.p>

          <motion.p
            variants={itemVariants}
            className="font-mono text-sm md:text-base text-gray-200 leading-8 max-w-2xl mb-8"
          >
            {t('hero.positioning')}
          </motion.p>

          <motion.div variants={itemVariants} className="mb-10">
            <h3 className="font-pixel text-arcade-pink text-sm md:text-lg mb-4">
              {t('hero.educationLabel')}
            </h3>
            <div className="space-y-3 max-w-2xl">
              <div className="flex items-center gap-4 border border-arcade-cyan/30 bg-arcade-panel/50 px-4 py-3">
                <span className="font-pixel text-[9px] md:text-[10px] text-arcade-cyan border border-arcade-cyan/70 px-2 py-1.5 shrink-0">
                  {t('hero.educationMasterDegree')}
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-sm md:text-base text-white">
                    {t('hero.educationMasterTitle')}
                  </p>
                  <p className="font-mono text-xs md:text-sm text-arcade-purple">
                    {t('hero.educationSchool')}
                  </p>
                </div>
                <span className="ml-auto font-pixel text-[8px] md:text-[9px] text-arcade-yellow whitespace-nowrap shrink-0">
                  ■ {t('hero.educationStatusOngoing')}
                </span>
              </div>

              <div className="flex items-center gap-4 border border-arcade-pink/30 bg-arcade-panel/50 px-4 py-3">
                <span className="font-pixel text-[9px] md:text-[10px] text-arcade-pink border border-arcade-pink/70 px-2 py-1.5 shrink-0">
                  {t('hero.educationBachelorDegree')}
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-sm md:text-base text-white">
                    {t('hero.educationBachelorTitle')}
                  </p>
                  <p className="font-mono text-xs md:text-sm text-arcade-purple">
                    {t('hero.educationSchool')} · {t('hero.educationBachelorNote')}
                  </p>
                </div>
                <span className="ml-auto font-pixel text-[8px] md:text-[9px] text-arcade-green whitespace-nowrap shrink-0">
                  ● {t('hero.educationStatusDone')}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="flex items-center gap-6">
            <span className="hidden md:inline-block w-2.5 h-2.5 bg-arcade-yellow -translate-y-6"></span>
            <a
              href="#projects"
              className="font-pixel text-xs md:text-sm bg-arcade-cyan text-arcade-bg px-6 py-4 rounded-md border border-arcade-cyan shadow-[0_0_14px_rgba(77,232,244,0.5)] hover:bg-arcade-cyan/80 transition-colors"
            >
              {t('hero.pressStart')} ▸
            </a>
            <a
              href="https://linkedin.com/in/nurionacak"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="flex items-center justify-center w-12 h-12 md:w-[52px] md:h-[52px] text-arcade-cyan text-xl md:text-2xl border border-arcade-cyan/60 rounded-md hover:bg-arcade-cyan/10 hover:shadow-[0_0_14px_rgba(77,232,244,0.4)] transition-all"
            >
              <FaLinkedin />
            </a>
            <a
              href="https://github.com/IRUNBOSS"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="flex items-center justify-center w-12 h-12 md:w-[52px] md:h-[52px] text-arcade-pink text-xl md:text-2xl border border-arcade-pink/60 rounded-md hover:bg-arcade-pink/10 hover:shadow-[0_0_14px_rgba(255,46,126,0.4)] transition-all"
            >
              <FaGithub />
            </a>
          </motion.div>
        </div>

        {/* Right: key art / photo */}
        <motion.div variants={itemVariants} className="hidden lg:block">
          <div className="relative border-2 border-arcade-pink rounded-lg shadow-[0_0_18px_rgba(255,46,126,0.35)] bg-arcade-panel/60 aspect-[3/4] overflow-hidden">
            <img
              src="/images/profile.jpg"
              alt="Nuri Onacak"
              className="w-full h-full object-cover"
            />
            <span className="absolute bottom-6 left-0 font-pixel text-xs text-white bg-arcade-pink px-4 py-2">
              PLAYER ONE
            </span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

export default Hero;
